// src/utils/elettrodottiOverpass.ts
// DOS518 – Layer Elettrodotti: fetch da Overpass API + classificazione tensione AIB

import * as L from 'leaflet';

// ─── Tipi ────────────────────────────────────────────────────────────────────

export type ClasseTensione = 'AAT' | 'AT' | 'MT' | 'BT' | 'sconosciuta';

export interface ElettrodottoProperties {
  osmId: string;
  voltageKV: number;        // kV normalizzato (0 se sconosciuto)
  voltageRaw: string;       // valore grezzo da OSM (es. "380000;220000")
  classe: ClasseTensione;
  bufferMetri: number;      // fascia di sicurezza per operazioni AIB
  name?: string;
  operator?: string;        // es. "Terna SpA"
  cables?: number;
  label: string;            // es. "380 kV (AAT)"
}

export interface ElettrodottoFeature {
  type: 'Feature';
  geometry: {
    type: 'LineString';
    coordinates: [number, number][]; // [lon, lat]
  };
  properties: ElettrodottoProperties;
}

export interface ElettrodottiGeoJSON {
  type: 'FeatureCollection';
  features: ElettrodottoFeature[];
  fetchedAt: number;        // timestamp ms
  bounds: { sw: [number, number]; ne: [number, number] };
}

// ─── Classificazione tensione ────────────────────────────────────────────────
//
// Buffer di sicurezza per operazioni AIB aeree (valori prudenziali):
//   AAT ≥ 220 kV (es. 380 kV Terna):   100 m
//   AT  60–219 kV (es. 132 kV):          60 m
//   MT  1–59 kV  (es. 15, 20 kV):        30 m
//   BT  < 1 kV:                           15 m
//   Sconosciuta:                           50 m (cautelativo)
//
// Nota: le DPA civili (DPCM 8/7/2003) sono molto inferiori, ma per
// elicotteri e Canadair in operazioni AIB si applicano questi valori.

export function classificaTensione(voltageStr?: string): {
  classe: ClasseTensione;
  voltageKV: number;
  bufferMetri: number;
} {
  if (!voltageStr || voltageStr.trim() === '') {
    return { classe: 'sconosciuta', voltageKV: 0, bufferMetri: 50 };
  }

  // OSM può avere "380000", "380000;220000", "380 kV", "380"
  const valori = voltageStr
    .split(';')
    .map(v => {
      const n = parseFloat(v.replace(/[^0-9.]/g, '').trim());
      return isNaN(n) ? 0 : n;
    })
    .filter(v => v > 0);

  if (valori.length === 0) {
    return { classe: 'sconosciuta', voltageKV: 0, bufferMetri: 50 };
  }

  const maxV = Math.max(...valori);
  // OSM usa valori in V (es. 380000) o kV (es. 380); normalizzo a kV
  const kv = maxV >= 1000 ? maxV / 1000 : maxV;

  if (kv >= 220) return { classe: 'AAT', voltageKV: kv, bufferMetri: 100 };
  if (kv >= 60)  return { classe: 'AT',  voltageKV: kv, bufferMetri: 60  };
  if (kv >= 1)   return { classe: 'MT',  voltageKV: kv, bufferMetri: 30  };
  return { classe: 'BT', voltageKV: kv, bufferMetri: 15 };
}

// ─── Parsing elemento Overpass ────────────────────────────────────────────────

function overpassWayToFeature(element: any): ElettrodottoFeature | null {
  if (element.type !== 'way' || !element.geometry || element.geometry.length < 2) {
    return null;
  }

  const coords: [number, number][] = element.geometry.map(
    (pt: { lat: number; lon: number }) => [pt.lon, pt.lat]
  );

  const tags = element.tags || {};
  const { classe, voltageKV, bufferMetri } = classificaTensione(tags.voltage);

  const label =
    voltageKV > 0
      ? `${Math.round(voltageKV)} kV (${classe})`
      : `Elettrodotto (${classe})`;

  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: coords },
    properties: {
      osmId: `osm_way_${element.id}`,
      voltageKV,
      voltageRaw: tags.voltage || '',
      classe,
      bufferMetri,
      label,
      name: tags.name,
      operator: tags.operator,
      cables: tags.cables ? parseInt(tags.cables, 10) : undefined,
    },
  };
}

// ─── Fetch da Overpass API ────────────────────────────────────────────────────

const OVERPASS_ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
];

export async function fetchElettrodottiOverpass(
  bounds: L.LatLngBounds,
  signal?: AbortSignal
): Promise<ElettrodottiGeoJSON> {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();

  // Espando il bbox del 15% per catturare linee che entrano nell'area
  const dLat = (ne.lat - sw.lat) * 0.15;
  const dLng = (ne.lng - sw.lng) * 0.15;
  const bbox = `${sw.lat - dLat},${sw.lng - dLng},${ne.lat + dLat},${ne.lng + dLng}`;

  const query = `[out:json][timeout:30];
(
  way["power"="line"](${bbox});
  way["power"="cable"](${bbox});
);
out geom;`;

  let lastError: Error | null = null;

  for (const endpoint of OVERPASS_ENDPOINTS) {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        body: query,
        signal,
        headers: { 'Content-Type': 'text/plain' },
      });

      if (!response.ok) {
        throw new Error(`Overpass HTTP ${response.status} da ${endpoint}`);
      }

      const data = await response.json();
      const features: ElettrodottoFeature[] = (data.elements || [])
        .map(overpassWayToFeature)
        .filter((f: ElettrodottoFeature | null): f is ElettrodottoFeature => f !== null);

      return {
        type: 'FeatureCollection',
        features,
        fetchedAt: Date.now(),
        bounds: { sw: [sw.lat, sw.lng], ne: [ne.lat, ne.lng] },
      };
    } catch (err: any) {
      if (err.name === 'AbortError') throw err;
      lastError = err;
      // Prova il prossimo endpoint
    }
  }

  throw lastError ?? new Error('Tutti gli endpoint Overpass non raggiungibili');
}
